/**
 * CaseBriefing — overlay inicial al cargar un caso.
 *
 * Antes: el alumno veía el grafo y no sabía qué hacer; probaba clicks random.
 * Ahora: recibe situación clínica explícita, misión, y rubric antes de entrar.
 * Esto convierte una exploración en un ejercicio con objetivo.
 *
 * El briefing se skippea por caso (localStorage) para que el profesor o el
 * alumno que quiere repetir no tenga que re-leer cada vez. Botón "Repetir
 * briefing" en el sidebar para volver a verlo.
 */

import type { PatientGraph, TriagePriority } from '../types/medical';

const OVERLAY_ID = 'cgt-briefing';
const STYLE_ID = 'cgt-briefing-styles';
const SEEN_KEY = 'cgt_briefed_cases';

const TRIAGE_LABEL: Record<TriagePriority, string> = {
  immediate: 'INMEDIATO (código rojo)',
  delayed: 'PUEDE ESPERAR (ámbar)',
  minor: 'AMBULATORIO (verde)',
  expectant: 'EXPECTANTE (gris)',
  deceased: 'FALLECIDO (negro)',
};

const TRIAGE_COLOR: Record<TriagePriority, string> = {
  immediate: '#FF453A',
  delayed: '#FF9F0A',
  minor: '#30D158',
  expectant: '#8E8E93',
  deceased: '#3A3A3C',
};

const SEX_LABEL: Record<string, string> = {
  male: 'masculino',
  female: 'femenino',
  other: 'otro',
};

export class CaseBriefing {
  private root: HTMLElement | null = null;

  /**
   * Muestra el briefing para un caso. Si el alumno ya lo vio y no fuerza
   * el flag `force`, no se muestra.
   */
  show(patient: PatientGraph, onStart?: () => void, force = false): void {
    if (this.root) return;
    if (!force && this.wasSeen(patient.id)) {
      onStart?.();
      return;
    }
    this.injectStyles();

    const triagePretty = TRIAGE_LABEL[patient.triagePriority];
    const color = TRIAGE_COLOR[patient.triagePriority];
    const demo = patient.demographics;
    const sex = SEX_LABEL[demo.sex] ?? demo.sex;

    // Cuento nodos por tipo para dar la "textura" del caso al alumno.
    const byType = new Map<string, number>();
    patient.nodes.forEach(n => byType.set(n.type, (byType.get(n.type) ?? 0) + 1));
    const counts = [
      { t: 'vital_sign', l: 'vitales', icon: '❤️' },
      { t: 'symptom', l: 'síntomas', icon: '🤕' },
      { t: 'laboratory', l: 'labs', icon: '🧪' },
      { t: 'imaging', l: 'imágenes', icon: '📷' },
      { t: 'history', l: 'antecedentes', icon: '📋' },
    ]
      .filter(c => (byType.get(c.t) ?? 0) > 0)
      .map(c => `<li>${c.icon} ${byType.get(c.t)} ${c.l}</li>`)
      .join('');

    const root = document.createElement('div');
    root.id = OVERLAY_ID;
    root.innerHTML = `
      <div class="cgt-brief-backdrop"></div>
      <article class="cgt-brief-card">
        <header>
          <span class="cgt-brief-tag" style="background:${color}20;color:${color};border:0.5px solid ${color}60">
            ${esc(triagePretty)}
          </span>
          <h1>${esc(patient.chiefComplaint ?? 'Nuevo caso clínico')}</h1>
        </header>

        <section class="cgt-brief-patient">
          <h2>Paciente</h2>
          <p><strong>${demo.age} años</strong>, ${esc(sex)}${demo.weight ? ` · ${demo.weight} kg` : ''}${demo.height ? ` · ${demo.height} cm` : ''}.</p>
        </section>

        <section class="cgt-brief-findings">
          <h2>Datos disponibles</h2>
          <ul>${counts}</ul>
        </section>

        <section class="cgt-brief-mission">
          <h2>Tu misión</h2>
          <ol>
            <li>Explorá el grafo y revisá los hallazgos.</li>
            <li>Proponé tu <strong>diagnóstico diferencial</strong> antes de consultar la IA.</li>
            <li>Asigná el <strong>triage</strong> correcto.</li>
            <li>Consultá al <strong>Tutor socrático</strong> si necesitás orientación.</li>
          </ol>
          <p class="cgt-brief-hint">💡 La IA no está para darte la respuesta. Está para validar tu razonamiento.</p>
        </section>

        <footer>
          <label class="cgt-brief-skip">
            <input type="checkbox" data-skip /> No volver a mostrar este briefing
          </label>
          <button type="button" class="cgt-brief-start" data-start>Comenzar ejercicio →</button>
        </footer>
      </article>
    `;

    root.querySelector<HTMLButtonElement>('[data-start]')?.addEventListener('click', () => {
      const skip = root.querySelector<HTMLInputElement>('[data-skip]')?.checked ?? false;
      if (skip) this.markSeen(patient.id);
      this.close();
      onStart?.();
    });

    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('cgt-brief-visible'));
    this.root = root;
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-brief-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  private wasSeen(caseId: string): boolean {
    try {
      const raw = localStorage.getItem(SEEN_KEY);
      const list: string[] = raw ? JSON.parse(raw) : [];
      return list.includes(caseId);
    } catch {
      return false;
    }
  }

  private markSeen(caseId: string): void {
    try {
      const raw = localStorage.getItem(SEEN_KEY);
      const list: string[] = raw ? JSON.parse(raw) : [];
      if (!list.includes(caseId)) list.push(caseId);
      localStorage.setItem(SEEN_KEY, JSON.stringify(list));
    } catch { /* ignore */ }
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${OVERLAY_ID} {
        position: fixed; inset: 0; z-index: 14000;
        display: flex; align-items: center; justify-content: center;
        opacity: 0; transition: opacity 0.25s;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      }
      #${OVERLAY_ID}.cgt-brief-visible { opacity: 1; }
      #${OVERLAY_ID} .cgt-brief-backdrop {
        position: absolute; inset: 0;
        background: radial-gradient(circle at 50% 30%, rgba(20,30,60,0.85) 0%, rgba(0,0,0,0.95) 80%);
        backdrop-filter: blur(8px);
      }
      #${OVERLAY_ID} .cgt-brief-card {
        position: relative;
        width: min(640px, 92vw);
        background: #0a0a0a;
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 18px;
        padding: 28px 32px;
        box-shadow: 0 24px 80px rgba(0,0,0,0.6);
        color: #f5f5f7;
        max-height: 88vh; overflow-y: auto;
      }
      #${OVERLAY_ID} header { margin-bottom: 20px; }
      #${OVERLAY_ID} .cgt-brief-tag {
        display: inline-block; padding: 4px 12px;
        font-size: 10.5px; font-weight: 700; letter-spacing: 1px;
        text-transform: uppercase; border-radius: 14px;
        margin-bottom: 10px;
      }
      #${OVERLAY_ID} h1 {
        font-size: 22px; font-weight: 700; line-height: 1.25;
        letter-spacing: -0.3px; color: #f5f5f7;
      }
      #${OVERLAY_ID} section { margin-bottom: 16px; }
      #${OVERLAY_ID} h2 {
        font-size: 10.5px; font-weight: 600; letter-spacing: 1.2px;
        text-transform: uppercase; color: #6e6e73; margin-bottom: 6px;
      }
      #${OVERLAY_ID} p { font-size: 13.5px; line-height: 1.5; color: #d1d1d6; }
      #${OVERLAY_ID} strong { color: #f5f5f7; font-weight: 600; }
      #${OVERLAY_ID} .cgt-brief-findings ul {
        list-style: none; padding: 0; margin: 0;
        display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        gap: 6px;
      }
      #${OVERLAY_ID} .cgt-brief-findings li {
        padding: 6px 10px;
        background: rgba(255,255,255,0.03);
        border: 0.5px solid rgba(255,255,255,0.06);
        border-radius: 8px;
        font-size: 12px; color: #d1d1d6;
      }
      #${OVERLAY_ID} .cgt-brief-mission ol {
        padding-left: 22px; margin: 0;
      }
      #${OVERLAY_ID} .cgt-brief-mission li {
        padding: 3px 0; font-size: 13px; color: #d1d1d6; line-height: 1.5;
      }
      #${OVERLAY_ID} .cgt-brief-hint {
        margin-top: 10px; padding: 10px 12px;
        background: rgba(255,159,10,0.1);
        border-left: 2px solid #FF9F0A; border-radius: 6px;
        font-size: 12px; color: #a1a1a6;
      }
      #${OVERLAY_ID} footer {
        display: flex; align-items: center; justify-content: space-between;
        margin-top: 18px; padding-top: 16px;
        border-top: 0.5px solid rgba(255,255,255,0.06);
        flex-wrap: wrap; gap: 12px;
      }
      #${OVERLAY_ID} .cgt-brief-skip {
        display: inline-flex; align-items: center; gap: 6px;
        font-size: 11.5px; color: #8e8e93; cursor: pointer;
      }
      #${OVERLAY_ID} .cgt-brief-skip input { accent-color: #2997FF; }
      #${OVERLAY_ID} .cgt-brief-start {
        padding: 12px 24px;
        background: linear-gradient(135deg, #2997FF, #BF5AF2);
        color: #fff; border: none; border-radius: 10px;
        font-size: 14px; font-weight: 700; cursor: pointer;
        font-family: inherit;
        transition: transform 0.1s, filter 0.15s;
      }
      #${OVERLAY_ID} .cgt-brief-start:hover { filter: brightness(1.1); }
      #${OVERLAY_ID} .cgt-brief-start:active { transform: scale(0.98); }
    `;
    document.head.appendChild(style);
  }
}

function esc(text: string): string {
  return text.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] ?? c));
}

export const caseBriefing = new CaseBriefing();
